import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  computed,
  inject,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalData,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'
import { EnterSubmitFormDirective } from '@flowx/designer/shared/directives/enter-submit-form.directive'
import { Enumeration } from '@flowx/designer/shared/entities/enumeration.entity'
import { KeyParametersRegex } from '@flowx/designer/shared/entities/utils'
import { ElementTypeEnum } from '@flowx/designer/shared/services/audit.service'
import { EnumerationsService } from '@flowx/designer/shared/services/enumerations.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

@Component({
  selector: 'flx-new-enumeration-modal',
  imports: [
    FlxModalModule,
    FlxIconModule,
    FlxFormControlModule,
    FormsModule,
    ReactiveFormsModule,
    FlxSpinnerModule,
    FlxButtonModule,
    EnterSubmitFormDirective,
    FlxAutofocusDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './new-enumeration-modal.component.html',
})
export class FlxNewEnumerationModalComponent implements OnInit {
  #modalService = inject(FlxModalService)
  #toastMsgService = inject(ToastMessageService)
  #enumerationsService = inject(EnumerationsService)
  #data: FlxModalData = inject(FLX_MODAL_DATA)
  #existingEnumeration: Enumeration = this.#data?.enumeration
  @Output() dataEventEmitter = new EventEmitter<Enumeration>()

  readOnly = this.#data?.readOnly
  loading = false

  enumerationNameFC = new FormControl<string>({ value: null, disabled: this.readOnly }, [
    Validators.required,
    Validators.pattern(KeyParametersRegex),
  ])

  enumerationForm = inject(FormBuilder).group({
    enumerationName: this.enumerationNameFC,
  })

  modalTitle = computed(() => {
    if (this.readOnly) {
      return 'View enumeration'
    }

    if (this.#existingEnumeration) {
      return 'Edit enumeration'
    }

    return 'Create enumeration'
  })

  saveButton = this.#existingEnumeration ? 'Update' : 'Create'

  ngOnInit(): void {
    if (this.#existingEnumeration) {
      this.#patchForm()
    }
  }

  submitModal(): void {
    if (this.#existingEnumeration) {
      this.dataEventEmitter.emit({
        ...this.#existingEnumeration,
        description: {
          ...this.#existingEnumeration.description,
          name: this.enumerationNameFC.value,
        },
      })
    } else {
      this.loading = true
      this.#createEnumeration({
        description: {
          name: this.enumerationNameFC.value,
          application: 'flowx',
          version: 1,
          draft: false,
          type: ElementTypeEnum.ENUMERATION,
        },
        contentValues: [],
      })
    }
  }

  closeModal(): void {
    this.#modalService.hide()
  }

  #patchForm(): void {
    this.enumerationNameFC.patchValue(this.#existingEnumeration.description.name)
  }

  #createEnumeration(enumeration: Partial<Enumeration>): void {
    this.#enumerationsService.createApplicationEnumeration(enumeration).subscribe({
      next: (response) => {
        this.loading = false
        this.#toastMsgService.showSuccessMessage('Enumeration created successfully')
        this.dataEventEmitter.emit(response as unknown as Enumeration)
      },
      error: (error) => {
        this.loading = false
        this.#toastMsgService.showErrorMessage(error.error.title ?? "Couldn't create enumeration!")
      },
    })
  }
}
